'use client';

import type React from 'react';
import ErrorBoundary from '@/components/ErrorBoundary';
import Header from './components/Header';
import {
  Suspense,
  useEffect,
} from 'react';
import OTP from './components/OTP';
import OrganizationSelect from './components/OrganizationSelect';
import {
  Dayjs,
} from 'dayjs';
import Policy from './components/Policy';
import useLoginProvider, {
  LoginProvider,
} from '@/contexts/LoginContext';
import isNil from 'lodash/isNil';
import Login from './components/Login';
import {
  useAuth,
} from '@/contexts/AuthContext';
import {
  useRouter,
  useSearchParams,
} from 'next/navigation';
import FullScreenSpinner from '@/core/components/FullScreenSpinner';

type PageState = 'login' | 'otp' | 'orgSelection';

export type LoginFormData = {
  firstName: string;
  lastName: string;
  dateOfBirth: Dayjs | undefined;
  phoneNumber: string;
}

export type OTPFormData = {
  otp: string;
}

export type OrgSelectionFormData = {
  org: string;
}

export type FormData = LoginFormData & OTPFormData & OrgSelectionFormData;

const PageByState: Record<PageState, React.ComponentType> = {
  login: Login,
  otp: OTP,
  orgSelection: OrganizationSelect,
};

function LoginPageContent() {

  const { isAuthenticated, isLoading: authLoading, error } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();

  const {
    organizations,
    tokenResponse,
    pageState,
    setPageState,
    handleOrgSelection,
  } = useLoginProvider();

  const PageComponent = PageByState[pageState];

  useEffect(() => {
    if (!isNil(organizations) && !isNil(tokenResponse) && organizations?.length === 1 ) {
      handleOrgSelection(organizations[0], tokenResponse);
    } else if (!isNil(organizations) && organizations?.length > 1) {
      setPageState('orgSelection');
    }
  }, [organizations, tokenResponse]);

  useEffect(() => {
    document.body.classList.add('login-page');
    document.documentElement.classList.add('login-page');

    return () => {
      document.body.classList.remove('login-page');
      document.documentElement.classList.remove('login-page');
    };
  }, []);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      const redirectPath = searchParams.get('redirect');
      const destination = redirectPath ? decodeURIComponent(redirectPath) : '/dashboard';
      router.replace(destination);
    }
  }, [isAuthenticated, authLoading, router, searchParams]);

  return authLoading ? (
    <FullScreenSpinner />
  ): isAuthenticated ? null: (
    <div className="fixed inset-0 flex flex-col items-center justify-center px-4 overflow-hidden">
      <div className="w-full max-w-[28rem] bg-white rounded-lg p-6 sm:p-10 pt-4 max-h-[95vh] overflow-y-auto flex flex-col shadow-[0_15px_35px_0_rgba(60,66,87,.08),_0_5px_15px_0_rgba(0,0,0,.12)]">
        <div className="flex flex-col gap-8">
          <Header />
          <PageComponent  />
        </div>
        <Policy />
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<FullScreenSpinner />}>
        <LoginProvider>
          <LoginPageContent />
        </LoginProvider>
      </Suspense>
    </ErrorBoundary>
  );
}
